export type MediaBlendMode = 'normal' | 'multiply' | 'screen' | 'overlay' | 'darken' | 'lighten';
export type MediaStabilizeLevel = 'minimum' | 'recommended' | 'strong';
export type MediaQualityLevel = 'hd' | 'uhd';
export type MediaNoiseReductionLevel = 'mild' | 'recommended' | 'strong';
export type MediaProcessingStatus = 'idle' | 'processing' | 'ready' | 'error';
export type CameraTrackingTarget = 'face' | 'body' | 'hand' | 'custom';
export type MotionBlurDirection = 'forward' | 'backward' | 'both';
export type MotionBlurSpeed = 'once' | 'twice' | '4x' | '6x';

export interface MediaProcessingState {
  status: MediaProcessingStatus;
  progress: number;
  error?: string | null;
}

export interface MediaEnhancements {
  blend?: { enabled: boolean; mode: MediaBlendMode } | null;
  stabilize?: {
    enabled: boolean;
    level: MediaStabilizeLevel;
    /** Browser-analyzed inverse camera offsets, relative to the clip start. */
    samples?: Array<{ tSeconds: number; x: number; y: number }> | null;
    processing?: MediaProcessingState | null;
  } | null;
  enhanceQuality?: { enabled: boolean; level: MediaQualityLevel; processing?: MediaProcessingState | null } | null;
  reduceNoise?: { enabled: boolean; level: MediaNoiseReductionLevel; processing?: MediaProcessingState | null } | null;
  cameraTracking?: {
    enabled: boolean;
    target: CameraTrackingTarget;
    shake: number;
    fitToCanvas: boolean;
    lockObjectSize: boolean;
    blurCanvas: boolean;
    customRegion?: { x: number; y: number; width: number; height: number } | null;
    processing?: MediaProcessingState | null;
  } | null;
  motionBlur?: {
    enabled: boolean;
    blur: number;
    blend: number;
    direction: MotionBlurDirection;
    speed: MotionBlurSpeed;
    processing?: MediaProcessingState | null;
  } | null;
}

export function mediaBlendOperation(settings: MediaEnhancements | null | undefined): GlobalCompositeOperation {
  if (!settings?.blend?.enabled) return 'source-over';
  const mode = settings.blend.mode;
  return mode === 'multiply' || mode === 'screen' || mode === 'overlay' || mode === 'darken' || mode === 'lighten'
    ? mode
    : 'source-over';
}

/**
 * A small safety crop is the local, deterministic stabilization pass. It removes
 * exposed edges while preserving the same geometry in preview and export.
 */
export function mediaStabilizationScale(settings: MediaEnhancements | null | undefined): number {
  if (!settings?.stabilize?.enabled) return 1;
  if (settings.stabilize.level === 'strong') return 1.08;
  if (settings.stabilize.level === 'minimum') return 1.02;
  return 1.045;
}

/** Resolve the analyzed stabilization offset at a clip-relative time. */
export function mediaStabilizationOffset(
  settings: MediaEnhancements | null | undefined,
  relativeTimeSeconds: number,
): { x: number; y: number } {
  const samples = settings?.stabilize?.enabled ? settings.stabilize.samples : null;
  if (!samples?.length) return { x: 0, y: 0 };
  const sorted = [...samples].sort((left, right) => left.tSeconds - right.tSeconds);
  const time = Math.max(0, Number.isFinite(relativeTimeSeconds) ? relativeTimeSeconds : 0);
  if (time <= sorted[0]!.tSeconds) return { x: sorted[0]!.x, y: sorted[0]!.y };
  const last = sorted[sorted.length - 1]!;
  if (time >= last.tSeconds) return { x: last.x, y: last.y };
  for (let index = 1; index < sorted.length; index++) {
    const right = sorted[index]!;
    if (right.tSeconds < time) continue;
    const left = sorted[index - 1]!;
    const span = Math.max(0.0001, right.tSeconds - left.tSeconds);
    const progress = clamp((time - left.tSeconds) / span, 0, 1);
    const eased = progress * progress * (3 - 2 * progress);
    return {
      x: round3(left.x + (right.x - left.x) * eased),
      y: round3(left.y + (right.y - left.y) * eased),
    };
  }
  return { x: last.x, y: last.y };
}

/** Canvas-native enhancement filters shared by the live preview and export worker. */
export function mediaEnhancementFilterCss(
  settings: MediaEnhancements | null | undefined,
  pixelScale = 1,
): string {
  if (!settings) return '';
  const filters: string[] = [];

  if (settings.reduceNoise?.enabled) {
    const blur = settings.reduceNoise.level === 'strong'
      ? 0.55
      : settings.reduceNoise.level === 'mild' ? 0.18 : 0.34;
    filters.push(`blur(${round3(blur * Math.max(0.1, pixelScale))}px)`);
  }

  if (settings.enhanceQuality?.enabled) {
    if (settings.enhanceQuality.level === 'uhd') {
      filters.push('contrast(1.08)', 'saturate(1.06)');
    } else {
      filters.push('contrast(1.04)', 'saturate(1.03)');
    }
  }

  if (settings.motionBlur?.enabled && settings.motionBlur.blur > 0) {
    const blur = clamp(settings.motionBlur.blur, 0, 100) * 0.055;
    filters.push(`blur(${round3(blur * Math.max(0.1, pixelScale))}px)`);
  }

  return filters.join(' ');
}

export function mediaMotionBlurAlpha(settings: MediaEnhancements | null | undefined): number {
  return settings?.motionBlur?.enabled ? clamp(settings.motionBlur.blend, 0, 100) / 100 : 0;
}

export function mediaTrackingBackgroundBlur(settings: MediaEnhancements | null | undefined): boolean {
  return settings?.cameraTracking?.enabled === true && settings.cameraTracking.blurCanvas === true;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}
